import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import { useAppSelector } from "../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import { useCanManageCustomFieldsQuery } from "../../../../../slices/CustomFieldsSlice";
import { useAddRoleMutation } from "../../../../../slices/RoleSlice";
import { SelectOptionType } from "../../../../../types/types";
import { NoAccessModal } from "../../../../customFields/CustomFieldModals";
import { RoleModal } from "../../../../settings/components/RoleModal";
import { useRoleUserOptions } from "../../../../settings/roles/useRoleUserOptions";

/** First option of the New work Role dropdown (blue "+" style of CustomSelect). */
export const CREATE_ROLE_OPTION: SelectOptionType = {
  value: "create",
  label: "Create role",
};

type CreatedRole = { id?: string; roleName?: string };

/**
 * "Create role" from the New work Role dropdown. Only people with Settings
 * access (Admin or "View Settings" — the same check as custom fields) can
 * create a role; everyone else gets the "You don't have access" popup.
 */
export const useCreateRole = (onCreated: (role: SelectOptionType) => void) => {
  const orgId = getLinkedFirm()?.orgId;
  const user = useAppSelector((state) => state.appReducer.user);
  const [isOpen, setIsOpen] = useState(false);
  const [noAccess, setNoAccess] = useState(false);

  const { data: canManage } = useCanManageCustomFieldsQuery(
    { orgId: orgId || "" },
    { skip: !orgId },
  );
  const { userOptions, isLoadingUsers } = useRoleUserOptions(
    isOpen ? orgId : undefined,
  );
  const [addRole, { isLoading: isAddingRole }] = useAddRoleMutation();

  const startCreateRole = () =>
    canManage ? setIsOpen(true) : setNoAccess(true);

  const handleCreate = async (roleName: string, selectedUserIds: string[]) => {
    if (!orgId || !user?.id) {
      return;
    }

    try {
      const created = (await addRole({
        roleName,
        orgId,
        createdByUserId: user.id,
        userIds: selectedUserIds,
      }).unwrap()) as CreatedRole | undefined;
      toast("Role successfully created", { type: "success" });
      setIsOpen(false);
      if (created?.id) {
        onCreated({ value: created.id, label: created.roleName || roleName });
      }
    } catch {
      toast("Something went wrong", { type: "error" });
    }
  };

  const createRoleModals = (
    <>
      {isOpen &&
        // Portal: the New work window would clip the role window, and its
        // submit must not reach the New work form (React bubbles it).
        createPortal(
          <div onSubmit={(event) => event.stopPropagation()}>
            <RoleModal
              onClose={() => setIsOpen(false)}
              userOptions={userOptions}
              isLoading={isAddingRole || isLoadingUsers}
              onSave={(roleName, selectedUserIds) =>
                void handleCreate(roleName, selectedUserIds)
              }
            />
          </div>,
          document.body,
        )}
      {noAccess && <NoAccessModal onClose={() => setNoAccess(false)} />}
    </>
  );

  return { startCreateRole, createRoleModals };
};
